import { assocPath, prop, sortBy } from 'ramda';
import {
    createContext,
    type ReactNode,
    type SyntheticEvent,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';
import { useGraph } from '../../../../../js/components/graph/GraphProvider';
import {
    createNewOutcomeAndNewMapElement,
    filterOutNotes,
} from '../../../../../js/components/graph/utils';
import { useCollaboration } from '../../../../collaboration/CollaborationProvider';
import {
    COLLABORATION_ITEM_TYPES,
    FLOW_EDITING_TOKEN,
    MAP_ELEMENT_TYPES,
} from '../../../../constants/graph';
import {
    BREADCRUMB_ACTION_TYPES,
    type BreadcrumbState,
    createBreadcrumbReducer,
} from '../../../../reducers/breadcrumbs';
import {
    getMapElement,
    type MapElementRequestAPI,
    saveMapElement,
} from '../../../../sources/graph';
import type {
    FlowGraphMapElementResponseAPI,
    FormalValueReference,
    MapElement,
} from '../../../../types/graph';
import { clearApprovalOutcomeAssignment } from '../../../../utils/approval';
import { isNullOrEmpty } from '../../../../utils/guard';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';
import { useUser } from '../../../UserProvider';
import { FRIENDLY_SCREEN_NAMES } from '../common/constants';
import screens, { type Screen } from '../common/screens';

type Selector = {
    selectedValueMeta?: { id: string; typeElementPropertyId: string | null };
    formalValueId?: string;
    valueElementInSubflowId: { id: string | null };
    valueElementToApplyId: { id: string | null; typeElementPropertyId: string | null };
};

export type MapElementContext = {
    id: string | null;
    title: string | null;
    elementType: string;
    onHide: () => void;
    container: HTMLElement | null;
    currentUserId: string;
    editingToken: string;
    flowId: string;
    mapElement: MapElement;
    currentScreen: Screen;
    breadcrumbs: BreadcrumbState<string>;
    onReturnToDefaultScreen: () => void;
    onSaveMapElement: (mapElement: MapElementRequestAPI) => void;
    onClose: () => void;
    onUpdateName: (name: string) => void;
    onUpdateTitle: (title: string) => void;
    onUpdateComments: (comments: string) => void;
    onUpdateStatusMessage: (message: string) => void;
    onUpdateNotAuthorizedMessage: (message: string) => void;
    onUpdateSubFlowArguments: (selectors: FormalValueReference[]) => void;
    onPushBreadCrumb: (id: Screen, content: string) => void;
    onSetActiveBreadCrumb: (activeItemId: Screen) => void;
    setMapElement: (mapElement: MapElement) => void;
    onSwitchScreen: (screen: Screen) => void;
    setConfigTitle: (title: string | null) => void;
    mapElements: MapElement[];
    onEditOutcome: (index: number) => void;
    onDeleteOutcome: (index: number) => void;
    selectedOutcomeIndex: number | null;
    setSelectedOutcomeIndex: (index: number | null) => void;
    defaultScreen: Screen;
    onEditNavigation: (index: number) => void;
    onDeleteNavigation: (index: number) => void;
    selectedNavigationIndex: number | null;
    setSelectedNavigationIndex: (index: number | null) => void;
};

export type MapElementConfigProps = {
    outcomeId: string | null;
    nextMapElementId: string | null;
    groupElementId?: string | null | undefined;
    mapElements: FlowGraphMapElementResponseAPI[];
    id: string | null;
    x: number;
    y: number;
    flowId: string;
    container: HTMLElement | null;
    dismissMapElementConfig: () => void;
    fromMapElementId?: string | null | undefined;
    setIsLoading: (isLoading: boolean) => void;
    refreshFlow: () => void;
    focusAndSelectElement: (id: string) => void;
    children?: ReactNode;
};

export type MapElementProviderProps = MapElementConfigProps & {
    defaultScreen: Screen;
    elementType: string;
    isLoading: boolean;
};

const Context = createContext<MapElementContext | undefined>(undefined);

/**
 * The core map element context provider.
 * Every map element needs to subscribe to this context.
 */
const MapElementProvider = ({
    groupElementId = null,
    defaultScreen,
    mapElements,
    elementType,
    id,
    x,
    y,
    flowId,
    container,
    children,
    dismissMapElementConfig,
    fromMapElementId = null,
    isLoading,
    setIsLoading,
    refreshFlow,
    focusAndSelectElement,
}: MapElementProviderProps) => {
    const user = useUser();
    const dispatchNotification = useNotificationDispatch();

    const currentUserId = user?.id;

    const [mapElementId, setMapElementId] = useState<string | null>(id);
    const [isLoadingMapElement, setIsLoadingMapElement] = useState(true);
    const [selectedOutcomeIndex, setSelectedOutcomeIndex] = useState<number | null>(null);
    const [selectedNavigationIndex, setSelectedNavigationIndex] = useState<number | null>(null);
    const [currentScreen, setCurrentScreen] = useState<Screen>(defaultScreen);
    const [configTitle, setConfigTitle] = useState<string | null>('');

    try {
        // biome-ignore lint/correctness/useHookAtTopLevel: Treat warnings as errors, fix later
        const graphContext = useGraph() as {
            isLoading: boolean;
            setIsLoading: (isLoading: boolean) => void;
        };
        isLoading = graphContext.isLoading;
        setIsLoading = graphContext.setIsLoading;
    } catch (_event) {
        // This component is not being used within the GraphProvider.
        // Do nothing.
    }

    const { invoke, subscribe, unsubscribe, itemOpened, itemClosed, itemChanged, getItem } =
        useCollaboration();

    const breadCrumbLabel =
        FRIENDLY_SCREEN_NAMES[defaultScreen as keyof typeof FRIENDLY_SCREEN_NAMES];

    const [breadcrumbs, setBreadcrumbs] = useReducer(createBreadcrumbReducer(), {
        trail: [
            {
                id: defaultScreen,
                content: breadCrumbLabel ?? '',
                onClick: () => onReturnToDefaultScreen(),
            },
        ],
        activeItemId: defaultScreen,
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        // biome-ignore lint/suspicious/noExplicitAny: changedMapElements is an object with each prop being a guid representing the ID of the map element
        const onGraphUpdate = (changedMapElements: any) => {
            if (mapElementId !== null) {
                const mapElement = changedMapElements[mapElementId];
                const item = getItem(mapElementId) as MapElement;
                if (mapElement && item) {
                    setMapElement({ ...item, x: mapElement.x, y: mapElement.y });
                }
            }
        };

        subscribe('GraphChanged', onGraphUpdate);
        return () => {
            unsubscribe('GraphChanged', onGraphUpdate);
        };
    }, [subscribe, unsubscribe, mapElementId, getItem]);

    const setMapElement = (newMapElement: MapElement) => {
        if (mapElementId) {
            itemChanged(mapElementId, 1, newMapElement, !!id);
        }
    };

    const onSwitchScreen = (screen: Screen) => {
        if (!Object.hasOwn(screens, screen)) {
            throw new Error(`The screen named: ${screen} is not valid`);
        }

        setCurrentScreen(screen);
    };

    const onPushBreadCrumb = (id: Screen, content: string) => {
        setBreadcrumbs({
            type: BREADCRUMB_ACTION_TYPES.push_breadcrumb,
            payload: {
                id,
                content,
                onClick: () => onSetActiveBreadCrumb(id),
            },
        });
    };

    const onSetActiveBreadCrumb = (activeItemId: Screen) => {
        setBreadcrumbs({
            type: BREADCRUMB_ACTION_TYPES.set_active_breadcrumb,
            payload: {
                activeItemId,
            },
        });
        onSwitchScreen(activeItemId);
    };

    const onReturnToDefaultScreen = () => {
        onSetActiveBreadCrumb(defaultScreen);
    };

    const onSaveMapElement = async (newMapElement: MapElementRequestAPI) => {
        setIsLoading(true);
        try {
            const newMapElementResponse = await saveMapElement(newMapElement, flowId);
            invoke('GraphChanged', flowId, [newMapElementResponse.id], null);
            onClose(null, newMapElementResponse.id);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }

        if (fromMapElementId) {
            await createNewOutcomeAndNewMapElement(
                fromMapElementId,
                mapElements,
                newMapElement.developerName,
                newMapElement.id,
                (mapElement: MapElementRequestAPI) => saveMapElement(mapElement, flowId),
                (mapElementId: string) => getMapElement(mapElementId, flowId),
                (error: unknown) =>
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    }),
            );
            onClose(null, fromMapElementId);
            setIsLoading(false);
        }
    };

    // Provide an optional ID that is used instead. Useful for new elements that don't have an ID yet.
    const onClose = (
        _: SyntheticEvent | null = null,
        elementId: string | null = id,
        refresh = true,
    ) => {
        if (refresh) {
            refreshFlow();
        }

        dismissMapElementConfig();
        if (elementId) {
            itemClosed(elementId, COLLABORATION_ITEM_TYPES.mapElement, !!elementId);
            focusAndSelectElement(elementId);
        }
    };

    const onUpdateName = (developerName: string) => {
        if (mapElementId) {
            setMapElement({ ...(getItem(mapElementId) as MapElement), developerName });
        }
    };

    const onUpdateTitle = (title: string) => {
        if (mapElementId) {
            setMapElement({ ...(getItem(mapElementId) as MapElement), title });
        }
    };

    const onUpdateComments = (developerSummary: string) => {
        if (mapElementId) {
            setMapElement({ ...(getItem(mapElementId) as MapElement), developerSummary });
        }
    };

    const onUpdateStatusMessage = (statusMessage: string) => {
        if (mapElementId) {
            setMapElement({ ...(getItem(mapElementId) as MapElement), statusMessage });
        }
    };

    const onUpdateNotAuthorizedMessage = (notAuthorizedMessage: string) => {
        if (mapElementId) {
            setMapElement({ ...(getItem(mapElementId) as MapElement), notAuthorizedMessage });
        }
    };

    const onUpdateSubFlowArguments = (selectors: FormalValueReference[]) => {
        const subFlowArguments = selectors
            ? selectors.reduce((acc, selector) => {
                  if (selector.selectedValueMeta) {
                      acc.push({
                          valueElementInSubflowId: {
                              id: selector.formalValueId || null,
                          },
                          valueElementToApplyId: {
                              id: selector.selectedValueMeta.id || null,
                              typeElementPropertyId:
                                  selector.selectedValueMeta.typeElementPropertyId || null,
                          },
                      });
                  }
                  return acc;
              }, [] as Selector[])
            : null;

        if (mapElementId) {
            setMapElement(
                assocPath(
                    ['subflow', 'arguments'],
                    subFlowArguments,
                    getItem(mapElementId) as MapElement,
                ),
            );
        }
    };

    const onEditOutcome = (outcomeIndex: number) => {
        setSelectedOutcomeIndex(outcomeIndex);
        onSwitchScreen(screens.outcome);
        onPushBreadCrumb(screens.outcome, screens.outcome);
    };

    const onDeleteOutcome = (outcomeIndex: number) => {
        if (mapElementId) {
            const mapElement = getItem(mapElementId) as MapElement;
            const outcomesArray = mapElement.outcomes ?? [];
            const outcomeToDelete = outcomesArray[outcomeIndex];

            let newMapElement: MapElement = {
                ...mapElement,
                outcomes: outcomesArray.filter((_, index) => index !== outcomeIndex),
            };

            // Clear approval outcome assignments if this was an approval outcome
            if (outcomeToDelete?.id && mapElement.elementType === MAP_ELEMENT_TYPES.approval) {
                const updatedElement = clearApprovalOutcomeAssignment(
                    newMapElement,
                    outcomeToDelete.id,
                );
                // Ensure outcomes remains an array after the update
                newMapElement = {
                    ...updatedElement,
                    outcomes: updatedElement.outcomes ?? [],
                };
            }

            setMapElement(newMapElement);
        }
    };

    const onEditNavigation = (navigationIndex: number) => {
        setSelectedNavigationIndex(navigationIndex);
        onSwitchScreen(screens.navigationOverrides);
        onPushBreadCrumb(screens.navigationOverrides, screens.navigationOverrides);
    };

    const onDeleteNavigation = (navigationIndex: number) => {
        if (mapElementId) {
            const mapElement = getItem(mapElementId) as MapElement;
            setMapElement({
                ...mapElement,
                navigationOverrides: (mapElement.navigationOverrides ?? []).filter(
                    (_, index) => index !== navigationIndex,
                ),
            });
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor include all dependencies
    useEffect(() => {
        const load = async () => {
            setIsLoading(true);

            let mapElement: MapElementRequestAPI | null = null;

            try {
                if (isNullOrEmpty(id)) {
                    mapElement = {
                        groupElementId,
                        elementType,
                        x,
                        y,
                        developerName: '',
                        id: crypto.randomUUID(),
                        dataActions: [],
                        messageActions: [],
                        listeners: [],
                        operations: [],
                        processes: [],
                        subflow: null,
                    };
                } else {
                    mapElement = await getMapElement(id, flowId);
                }

                if (!mapElement) {
                    return;
                }

                mapElement.operations = sortBy(prop('order'), mapElement?.operations ?? []);
                mapElement.dataActions = sortBy(prop('order'), mapElement?.dataActions ?? []);
                mapElement.messageActions = sortBy(prop('order'), mapElement?.messageActions ?? []);
                mapElement.processes = sortBy(prop('order'), mapElement?.processes ?? []);
                mapElement.listeners = mapElement?.listeners ?? [];
                mapElement.navigationOverrides = mapElement?.navigationOverrides ?? [];
                mapElement.outcomes = sortBy(prop('order'), mapElement?.outcomes ?? []);
                mapElement.approval = mapElement?.approval ?? null;

                setMapElementId(mapElement.id);

                itemOpened(
                    mapElement.id,
                    flowId,
                    COLLABORATION_ITEM_TYPES.mapElement,
                    mapElement,
                    !!id,
                );
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setIsLoading(false);
                setIsLoadingMapElement(false);
            }
        };

        load();
    }, []);

    const mapElement = getItem(mapElementId ?? '') as MapElement;

    if (mapElement) {
        mapElement.dataActions = sortBy(prop('order'), mapElement?.dataActions ?? []);
        mapElement.messageActions = sortBy(prop('order'), mapElement?.messageActions ?? []);
        mapElement.processes = sortBy(prop('order'), mapElement?.processes ?? []);
        mapElement.listeners = mapElement?.listeners ?? [];
        mapElement.navigationOverrides = mapElement?.navigationOverrides ?? [];
        mapElement.outcomes = sortBy(prop('order'), mapElement?.outcomes ?? []);
    }

    if (!currentUserId || isLoading || isLoadingMapElement) {
        return null;
    }

    const contextValue: MapElementContext = {
        id: id,
        title:
            configTitle && configTitle.length > 0 ? configTitle : (mapElement?.developerName ?? ''),
        elementType: mapElement?.elementType ?? elementType,
        onHide: onClose,
        container,
        currentUserId,
        editingToken: FLOW_EDITING_TOKEN,
        flowId,
        mapElement,
        currentScreen,
        breadcrumbs,
        onReturnToDefaultScreen,
        onSaveMapElement,
        onClose,
        onUpdateName,
        onUpdateTitle,
        onUpdateComments,
        onUpdateStatusMessage,
        onUpdateNotAuthorizedMessage,
        onUpdateSubFlowArguments,
        onPushBreadCrumb,
        onSetActiveBreadCrumb,
        setMapElement,
        onSwitchScreen,
        setConfigTitle,
        mapElements: filterOutNotes(mapElements),
        onEditOutcome,
        onDeleteOutcome,
        selectedOutcomeIndex,
        setSelectedOutcomeIndex,
        defaultScreen,
        onEditNavigation,
        onDeleteNavigation,
        selectedNavigationIndex,
        setSelectedNavigationIndex,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useMapElement = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useMapElement must be used within a MapElementProvider');
    }
    return context;
};

export { MapElementProvider, useMapElement };
